import { Outlet, redirect } from '@tanstack/react-router';
import { FeatureFlagGuard } from '@/components/guards';
import { AccountFeatureFlag, hasFeatureFlag } from '@/services/auth/feature-flags';
import { useLocation, useParams } from '@/services/router/compat';
import { designStudioDashboardUrl, designStudioEditorIndexTarget } from '@/services/router/design-studio-href';
import { lazyRoute } from '@/services/router/prefetch';
import { SafeNavigate } from '@/services/router/safe-navigate';
import { DesignStudioRoute, LastWorkspaceResolver, LazyPage } from './-route-layouts';

// Design Studio's editor pages, behind the `ds-editor-in-hydra` kill-switch. Flag on: `route-ownership.ts`
// claims these URLs for hydra and these routes render them. Flag off: ownership falls back to the
// embedded owner and each route renders the Design Studio bundle host instead, which routes the URL
// itself, exactly as before. Ownership and what renders flip together on the one flag.
//
// Until the editor moves in, the flag-on pages are placeholders: this wires up the routes, ownership
// and redirects so they can be checked in production before the editor itself lands behind them.

const DesignStudioComingSoonPage = lazyRoute(
  '/workspaces/:workspaceId/design-studio/editor/:id',
  () => import('@/pages/workspaces/design-studio/coming-soon/index.page'),
);

function comingSoon(page: string) {
  return function DesignStudioComingSoonRoute() {
    return <DesignStudioComingSoonPage page={page} />;
  };
}

export const DesignStudioEmailEditorRoute = comingSoon('Email editor');
export const DesignStudioWhatsappEditorRoute = comingSoon('WhatsApp editor');
export const DesignStudioInboxMessageEditorRoute = comingSoon('Inbox message editor');
export const DesignStudioSmsEditorRoute = comingSoon('SMS editor');
export const DesignStudioSignUpFormEditorRoute = comingSoon('Sign-up form editor');
export const DesignStudioInAppEditorRoute = comingSoon('In-app message editor');
export const DesignStudioInboxPreviewsRoute = comingSoon('Inbox previews');
export const DesignStudioInboxPreviewRoute = comingSoon('Inbox preview');
export const DesignStudioJourneysTemplateRoute = comingSoon('Journeys template');
export const DesignStudioBrandRoute = comingSoon('Brand');
export const DesignStudioAssetsRoute = comingSoon('Design Studio assets');

/** The layout around the workspace's Design Studio editor pages. */
export function DesignStudioEditorLayoutRoute() {
  return (
    <FeatureFlagGuard flag={AccountFeatureFlag.DsEditorInHydra} fallback={DesignStudioRoute}>
      <LastWorkspaceResolver>
        <LazyPage>
          <Outlet />
        </LazyPage>
      </LastWorkspaceResolver>
    </FeatureFlagGuard>
  );
}

type WorkspaceRouteContext = { params: { workspaceId: string } };

// The redirects run twice on purpose. `beforeLoad` handles in-app navigations, where the account is
// already loaded. On a cold page load `beforeLoad` runs before the account (so the flag reads off and
// it lets the route through); the matching component then redirects once the layout has the flag.

/** `beforeLoad` for `/design-studio/welcome`, which only leads to the dashboard. */
export function redirectToDesignStudioDashboard({ params }: WorkspaceRouteContext) {
  if (!hasFeatureFlag(AccountFeatureFlag.DsEditorInHydra)) {
    return;
  }

  throw redirect({ href: designStudioDashboardUrl(params.workspaceId), replace: true });
}

/** `beforeLoad` for `/design-studio/editor`: `?file=<id>` opens that file; without it, the dashboard. */
export function redirectFromEditorIndex({
  params,
  location,
}: WorkspaceRouteContext & { location: { searchStr: string } }) {
  if (!hasFeatureFlag(AccountFeatureFlag.DsEditorInHydra)) {
    return;
  }

  throw redirect({ href: designStudioEditorIndexTarget(params.workspaceId, location.searchStr), replace: true });
}

/** The component behind `redirectToDesignStudioDashboard`, for a cold page load. */
export function DesignStudioDashboardRedirectRoute() {
  const { workspaceId = 'last' } = useParams<{ workspaceId?: string }>();
  return <SafeNavigate to={designStudioDashboardUrl(workspaceId)} options={{ shouldReplaceHistoryEntry: true }} />;
}

/** The component behind `redirectFromEditorIndex`, for a cold page load. */
export function DesignStudioEditorIndexRedirectRoute() {
  const { workspaceId = 'last' } = useParams<{ workspaceId?: string }>();
  const { search } = useLocation();
  return (
    <SafeNavigate
      to={designStudioEditorIndexTarget(workspaceId, search)}
      options={{ shouldReplaceHistoryEntry: true }}
    />
  );
}
